import type { Recipe, ScalePreviewResponse } from '@recipes/contracts';
import { formatIngredientLine, scaleQuantity, scalingFactor } from '@recipes/domain';
import type { Locale } from '@/lib/i18n';

/**
 * Client-side scale preview used when the server is unreachable (offline cooking).
 * Same decimal math as the API; no unit conversion (that needs the density catalog).
 */
export function localScalePreview(
  recipe: Pick<Recipe, 'servings' | 'ingredientSections'>,
  requestedServings: string,
  locale: Locale,
): ScalePreviewResponse {
  const factor = scalingFactor(recipe.servings, requestedServings);
  return {
    originalServings: recipe.servings,
    requestedServings,
    factor,
    sections: recipe.ingredientSections.map((section) => ({
      id: section.id,
      title: section.title,
      ingredients: section.ingredients.map((ingredient) => {
        const scaled = scaleQuantity(ingredient, factor);
        const line = formatIngredientLine(
          {
            quantity: scaled.quantity,
            quantityMax: scaled.quantityMax,
            quantityText: ingredient.quantityText,
            unitCode: ingredient.unitCode,
            unitText: ingredient.unitText,
            name: ingredient.name,
          },
          locale,
        );
        return {
          id: ingredient.id,
          name: ingredient.name,
          note: ingredient.note,
          optional: ingredient.optional,
          scalable: ingredient.scalable,
          quantity: scaled.quantity,
          quantityMax: scaled.quantityMax,
          quantityText: ingredient.quantityText,
          unitCode: ingredient.unitCode,
          unitText: ingredient.unitText,
          display: line.amount,
          hint: line.hint,
          converted: false,
        };
      }),
    })),
  };
}
